import React, { useState, useRef } from 'react';
import { Player } from '@remotion/player';
import { ReelComposition } from '../video/ReelComposition';
import { FPS, WIDTH, HEIGHT, DURATION_PER_IMAGE, END_SCREEN_DURATION } from '../video/constants';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { formatCurrency } from '../utils/formatters';
import { getLogoBase64 } from '../utils/imageUtils';

const ReelModal = ({ isOpen, onClose, formData }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const playerRef = useRef(null);

  if (!isOpen) return null;

  const totalFrames = (formData.images.length * DURATION_PER_IMAGE + END_SCREEN_DURATION) * FPS;
  const totalSeconds = formData.images.length * DURATION_PER_IMAGE + END_SCREEN_DURATION;

  const handleExport = async () => {
    setIsExporting(true);
    setExportProgress(0);

    try {
      // 1. Crear un canvas oculto para renderizar frame por frame
      const canvas = document.createElement('canvas');
      canvas.width = WIDTH;
      canvas.height = HEIGHT;
      const ctx = canvas.getContext('2d');

      // 2. Precargar imágenes para evitar parpadeos
      const loadedImages = await Promise.all(
        formData.images.map(img => {
          return new Promise((resolve) => {
            const i = new Image();
            i.crossOrigin = "anonymous";
            i.onload = () => resolve(i);
            i.onerror = () => resolve(null);
            i.src = img.src;
          });
        })
      );

      // 3. Precargar Logotipo
      let logoImg = null;
      try {
        const base64Logo = await getLogoBase64();
        logoImg = new Image();
        logoImg.src = base64Logo;
        await new Promise((resolve) => {
          logoImg.onload = resolve;
          logoImg.onerror = resolve;
        });
      } catch (e) {
        console.warn("No se pudo cargar el logo para el video");
      }

      // 4. Configurar el Grabador de Video
      let mimeType = 'video/webm;codecs=vp9';
      let extension = 'webm';
      
      // Soporte para Safari y otros navegadores
      if (MediaRecorder.isTypeSupported('video/mp4')) {
        mimeType = 'video/mp4';
        extension = 'mp4';
      } else if (MediaRecorder.isTypeSupported('video/webm;codecs=vp8')) {
        mimeType = 'video/webm;codecs=vp8';
      }

      const stream = canvas.captureStream(FPS);
      const recorder = new MediaRecorder(stream, { mimeType });
      const chunks = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Reel_Arboleda_${formData.city}_${Date.now()}.${extension}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setIsExporting(false);
      };

      // Iniciar grabación
      recorder.start();

      let currentFrame = 0;
      const framesPerSlide = DURATION_PER_IMAGE * FPS;

      // Función auxiliar para dibujar rectángulos redondeados
      const drawRoundRect = (ctx, x, y, width, height, radius) => {
        ctx.beginPath();
        ctx.moveTo(x + radius, y);
        ctx.lineTo(x + width - radius, y);
        ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
        ctx.lineTo(x + width, y + height - radius);
        ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
        ctx.lineTo(x + radius, y + height);
        ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
        ctx.lineTo(x, y + radius);
        ctx.quadraticCurveTo(x, y, x + radius, y);
        ctx.closePath();
        ctx.fill();
      };

      // 5. Motor de Renderizado Frame por Frame
      const drawFrame = () => {
        if (currentFrame >= totalFrames) {
          recorder.stop();
          return;
        }

        // Limpiar fondo
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, WIDTH, HEIGHT);

        const isEndScreen = currentFrame >= formData.images.length * framesPerSlide;

        if (!isEndScreen) {
          // --- RENDERIZAR DIAPOSITIVA DE PROPIEDAD ---
          const slideIndex = Math.floor(currentFrame / framesPerSlide);
          const frameInSlide = currentFrame % framesPerSlide;
          const img = loadedImages[slideIndex];

          if (img) {
            // Efecto Zoom Lento (Scale)
            const scaleProgress = Math.min(frameInSlide / 120, 1);
            const scale = 1 + (0.12 * scaleProgress);
            const scaledWidth = WIDTH * scale;
            const scaledHeight = HEIGHT * scale;
            const x = (WIDTH - scaledWidth) / 2;
            const y = (HEIGHT - scaledHeight) / 2;
            ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
          }

          // Gradiente inferior
          const gradient = ctx.createLinearGradient(0, HEIGHT * 0.4, 0, HEIGHT);
          gradient.addColorStop(0, 'rgba(0,0,0,0)');
          gradient.addColorStop(1, 'rgba(0,0,0,0.95)');
          ctx.fillStyle = gradient;
          ctx.fillRect(0, 0, WIDTH, HEIGHT);

          // Marca de Agua (Top Left)
          if (logoImg) {
            ctx.fillStyle = 'rgba(255,255,255,0.95)';
            ctx.shadowColor = 'rgba(0,0,0,0.3)';
            ctx.shadowBlur = 20;
            if (ctx.roundRect) {
              ctx.beginPath(); ctx.roundRect(48, 48, 300, 100, 24); ctx.fill();
            } else {
              drawRoundRect(ctx, 48, 48, 300, 100, 24);
            }
            ctx.shadowBlur = 0; // Reset shadow
            ctx.drawImage(logoImg, 60, 60, 76, 76);
            ctx.fillStyle = '#022c22';
            ctx.font = '900 34px sans-serif';
            ctx.fillText('ARBOLEDA', 150, 95);
            ctx.fillStyle = '#059669';
            ctx.font = 'bold 16px sans-serif';
            ctx.fillText('BIENES RAÍCES', 150, 125);
          }

          // Textos de la Propiedad (Bottom Left)
          // Etiqueta "EN VENTA/RENTA"
          ctx.fillStyle = '#059669'; // bg-emerald-600
          if (ctx.roundRect) {
            ctx.beginPath(); ctx.roundRect(90, HEIGHT - 520, 280, 70, 20); ctx.fill();
          } else {
            drawRoundRect(ctx, 90, HEIGHT - 520, 280, 70, 20);
          }
          ctx.fillStyle = '#ffffff';
          ctx.font = '900 36px sans-serif';
          ctx.fillText(`EN ${formData.operation.toUpperCase()}`, 120, HEIGHT - 472);

          // Sombras para los textos principales
          ctx.shadowColor = 'rgba(0,0,0,0.8)';
          ctx.shadowBlur = 20;
          ctx.shadowOffsetY = 4;

          ctx.fillStyle = '#ffffff';
          ctx.font = '900 130px sans-serif';
          ctx.fillText(formData.propertyType, 90, HEIGHT - 310);

          ctx.fillStyle = '#34d399'; // text-emerald-400
          ctx.font = '900 95px sans-serif';
          ctx.fillText(formatCurrency(formData.price, formData.currency), 90, HEIGHT - 200);

          ctx.fillStyle = '#e5e7eb'; // text-gray-200
          ctx.font = '50px sans-serif';
          ctx.fillText(`📍 ${formData.city}, Honduras`, 90, HEIGHT - 120);
          
          ctx.shadowColor = 'transparent'; // Reset shadow

        } else {
          // --- RENDERIZAR PANTALLA FINAL (CONTACTO) ---
          const endFrame = currentFrame - (formData.images.length * framesPerSlide);
          const progress = Math.min(endFrame / 60, 1); // Animar durante los primeros 60 frames

          ctx.fillStyle = '#022c22'; // bg-emerald-950
          ctx.fillRect(0, 0, WIDTH, HEIGHT);

          ctx.save();
          // Animación de entrada (Scale + Fade)
          const scale = 0.8 + (0.2 * progress);
          ctx.translate(WIDTH / 2, HEIGHT / 2);
          ctx.scale(scale, scale);
          ctx.translate(-WIDTH / 2, -HEIGHT / 2);
          ctx.globalAlpha = Math.min(progress * 1.5, 1);

          // Caja Blanca del Logo
          ctx.fillStyle = '#ffffff';
          ctx.shadowColor = 'rgba(16, 185, 129, 0.2)'; // ring-emerald-500/20
          ctx.shadowBlur = 40;
          if (ctx.roundRect) {
            ctx.beginPath(); ctx.roundRect(WIDTH / 2 - 160, HEIGHT / 2 - 450, 320, 320, 60); ctx.fill();
          } else {
            drawRoundRect(ctx, WIDTH / 2 - 160, HEIGHT / 2 - 450, 320, 320, 60);
          }
          ctx.shadowBlur = 0;
          if (logoImg) ctx.drawImage(logoImg, WIDTH / 2 - 140, HEIGHT / 2 - 430, 280, 280);

          ctx.textAlign = 'center';
          ctx.fillStyle = '#34d399'; // text-emerald-400
          ctx.font = '900 45px sans-serif';
          ctx.fillText('CONTÁCTANOS', WIDTH / 2, HEIGHT / 2 - 20);

          ctx.fillStyle = '#ffffff';
          ctx.font = '900 90px sans-serif';
          ctx.fillText(formData.agentName || 'Agente Arboleda', WIDTH / 2, HEIGHT / 2 + 80);

          // Caja del Teléfono
          ctx.fillStyle = '#10b981'; // bg-emerald-500
          ctx.shadowColor = 'rgba(0,0,0,0.3)';
          ctx.shadowBlur = 20;
          if (ctx.roundRect) {
            ctx.beginPath(); ctx.roundRect(WIDTH / 2 - 350, HEIGHT / 2 + 130, 700, 120, 40); ctx.fill();
          } else {
            drawRoundRect(ctx, WIDTH / 2 - 350, HEIGHT / 2 + 130, 700, 120, 40);
          }
          ctx.shadowBlur = 0;
          ctx.fillStyle = '#022c22'; // text-emerald-950
          ctx.font = '900 70px sans-serif';
          ctx.fillText(formData.agentPhone || 'Teléfono no disp.', WIDTH / 2, HEIGHT / 2 + 215);

          ctx.fillStyle = '#6ee7b7'; // text-emerald-300
          ctx.font = 'bold 45px sans-serif';
          ctx.fillText(formData.agentEmail || 'contacto@arboleda.hn', WIDTH / 2, HEIGHT / 2 + 330);

          ctx.restore();
        }

        // Actualizar progreso UI y solicitar siguiente frame
        setExportProgress((currentFrame / totalFrames) * 100);
        currentFrame++;
        
        // Usamos setTimeout para dictar el ritmo de grabación al MediaRecorder
        setTimeout(drawFrame, 1000 / FPS);
      };

      // Iniciar el loop de dibujado
      drawFrame();

    } catch (err) {
      console.error("Error en motor de exportación:", err);
      alert("Hubo un error inesperado al procesar el video. Intenta nuevamente.");
      setIsExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 md:p-4 bg-black/90 backdrop-blur-md">
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-3xl overflow-hidden shadow-2xl max-w-5xl w-full flex flex-col md:flex-row h-[90vh] md:h-[85vh]"
      >
        {/* Lado del Video */}
        <div className="bg-black flex-1 flex items-center justify-center p-2 md:p-6 relative">
          <div className="h-full aspect-[9/16] shadow-2xl rounded-2xl overflow-hidden border border-white/10 relative remotion-player">
            <Player
              ref={playerRef}
              component={ReelComposition}
              inputProps={{ formData }}
              durationInFrames={totalFrames}
              fps={FPS}
              compositionWidth={WIDTH}
              compositionHeight={HEIGHT}
              style={{ width: '100%', height: '100%' }}
              controls={!isExporting}
              autoPlay
              loop={!isExporting}
            />
            
            <AnimatePresence>
              {isExporting && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute inset-0 bg-black/80 backdrop-blur-md flex flex-col items-center justify-center p-8 text-center z-50 rounded-2xl"
                >
                  <div className="w-24 h-24 mb-6 relative">
                    <svg className="w-full h-full transform -rotate-90 drop-shadow-lg">
                      <circle cx="48" cy="48" r="42" stroke="currentColor" strokeWidth="8" fill="transparent" className="text-gray-800" />
                      <circle 
                        cx="48" cy="48" r="42" stroke="currentColor" strokeWidth="8" fill="transparent" 
                        strokeDasharray={264}
                        strokeDashoffset={264 - (264 * exportProgress) / 100}
                        className="text-emerald-500 transition-all duration-300" 
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center font-black text-white text-lg">
                      {Math.round(exportProgress)}%
                    </div>
                  </div>
                  <h4 className="text-white font-black text-2xl mb-2 italic tracking-tight">RENDERIZANDO...</h4>
                  <p className="text-emerald-400 text-xs font-bold uppercase tracking-widest mb-4 animate-pulse">
                    Procesando {totalFrames} frames
                  </p>
                  <p className="text-gray-400 text-xs max-w-[200px]">
                    El motor interno está procesando el video. Por favor no cierres esta ventana.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Lado de Controles */}
        <div className="w-full md:w-[400px] flex flex-col bg-gray-50 border-l border-gray-100 h-full overflow-hidden">
          
          <div className="flex-1 overflow-y-auto p-6 md:p-8">
            <div className="flex justify-between items-center mb-8">
              <div>
                <h3 className="text-2xl font-black text-gray-900 tracking-tight">Exportar Reel</h3>
                <p className="text-emerald-600 text-[10px] font-black uppercase tracking-widest">Motor Canvas V2</p>
              </div>
              <button 
                onClick={onClose} 
                disabled={isExporting}
                className="p-2 bg-white border border-gray-200 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-30 shadow-sm"
              >
                <SafeIcon icon={FiIcons.FiX} className="text-xl" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="p-5 bg-white rounded-2xl border border-gray-200 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-emerald-100 text-emerald-700 rounded-lg">
                    <SafeIcon icon={FiIcons.FiSmartphone} />
                  </div>
                  <p className="text-xs font-black text-gray-400 uppercase tracking-wider">Formato Alta Calidad</p>
                </div>
                <p className="text-sm text-gray-700 font-bold">1080 x 1920 (9:16)</p>
                <p className="text-xs text-gray-500 mt-1">Optimizado para Instagram, TikTok y YouTube Shorts.</p>
              </div>

              <div className="p-5 bg-white rounded-2xl border border-gray-200 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-blue-100 text-blue-700 rounded-lg">
                    <SafeIcon icon={FiIcons.FiClock} />
                  </div>
                  <p className="text-xs font-black text-gray-400 uppercase tracking-wider">Duración Total</p>
                </div>
                <p className="text-sm text-gray-700 font-bold">{totalSeconds.toFixed(1)} segundos</p>
                <p className="text-xs text-gray-500 mt-1">{formData.images.length} escenas dinámicas + cierre.</p>
              </div>
            </div>

            <div className="mt-8 p-6 bg-emerald-900 rounded-2xl text-white">
              <h4 className="text-xs font-black uppercase tracking-widest text-emerald-400 mb-3">Instrucciones</h4>
              <ul className="text-[11px] space-y-3 font-medium opacity-90">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 font-bold">1.</span>
                  Presiona el botón de descarga en la parte inferior.
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 font-bold">2.</span>
                  El motor interno procesará la animación frame por frame (verás el progreso sobre el video).
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 font-bold">3.</span>
                  Al llegar al 100%, el archivo .webm o .mp4 se guardará automáticamente en tus descargas.
                </li>
              </ul>
            </div>
          </div>

          <div className="shrink-0 p-6 bg-white border-t border-gray-200 shadow-[0_-5px_15px_-5px_rgba(0,0,0,0.05)] z-10">
            <button 
              onClick={handleExport}
              disabled={isExporting}
              className={`w-full py-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-3 shadow-lg ${
                isExporting 
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                  : 'bg-emerald-600 hover:bg-emerald-700 text-white active:scale-95'
              }`}
            >
              {isExporting ? (
                <SafeIcon icon={FiIcons.FiLoader} className="animate-spin text-xl" />
              ) : (
                <SafeIcon icon={FiIcons.FiDownloadCloud} className="text-xl" />
              )}
              {isExporting ? `PROCESANDO ${Math.round(exportProgress)}%` : 'Descargar Video'}
            </button>
          </div>

        </div>
      </motion.div>
    </div>
  );
};

export default ReelModal;